import { Link } from "react-router-dom";
import { useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import collectionLiving from "@/assets/collection-living.webp";
import collectionBedroom from "@/assets/collection-bedroom.webp";
import collectionDining from "@/assets/collection-dining.webp";
import collectionOutdoor from "@/assets/collection-outdoor.webp";
import collectionSofas from "@/assets/collection-sofas.webp";
import collectionRugs from "@/assets/collection-rugs.webp";
import collectionChandeliers from "@/assets/collection-chandeliers.webp";
import collectionDecor from "@/assets/collection-decor.webp";
import collectionDiningTables from "@/assets/collection-dining-tables.webp";
import collectionOffice from "@/assets/collection-office.webp";
import collectionOfficeChairs from "@/assets/collection-office-chairs.webp";
import collectionBookcases from "@/assets/collection-bookcases.webp";
import collectionMirrors from "@/assets/collection-mirrors.webp";
import collectionWallDecor from "@/assets/collection-wall-decor.webp";
import collectionFloorLamps from "@/assets/collection-floor-lamps.webp";
import collectionTableLamps from "@/assets/collection-table-lamps.webp";
import collectionOutdoorRugs from "@/assets/collection-outdoor-rugs.webp";
import { MegaMenuDropdown } from "./MegaMenuDropdown";
import {
  isEmptyCategoryHref,
  useCategoryStockVersion,
  withoutEmptyCategories,
  withoutThinCategories,
} from "@/lib/categoryStock";

interface SubCategory {
  label: string;
  href: string;
}

interface MegaMenuColumn {
  heading: string;
  items: SubCategory[];
}

export interface FeaturedImage {
  src: string;
  alt: string;
  caption: string;
  href: string;
}

export interface MegaMenuItem {
  label: string;
  href: string;
  columns?: MegaMenuColumn[];
  images?: FeaturedImage[];
  highlight?: boolean;
}

// The one list of menu links: the phone and tablet menu in Header.tsx is built
// from it too (visibleMegaMenu), so the two menus cannot drift apart. Links to
// categories with too few products are dropped when the menu is shown, so keep
// small categories listed here: they return when stock does.
const megaMenuData: MegaMenuItem[] = [
  {
    label: "Living Room",
    href: "/category/living",
    images: [
      { src: collectionSofas, alt: "Sofas collection", caption: "New Sofas", href: "/category/sofas" },
    ],
    columns: [
      {
        heading: "Seating",
        items: [
          { label: "All Living Room", href: "/category/living" },
          { label: "Sofas", href: "/category/sofas" },
          { label: "Love Seats", href: "/category/love-seats" },
          { label: "Sectionals", href: "/category/sectionals" },
          { label: "Seating Sets", href: "/category/seating-sets" },
          { label: "Accent Chairs", href: "/category/accent-chairs" },
          { label: "Recliners", href: "/category/recliners" },
          { label: "Ottomans", href: "/category/ottomans" },
          { label: "Bean Bag Chairs", href: "/category/bean-bags" },
          { label: "Rocking Chairs", href: "/category/rocking-chairs" },
        ],
      },
      {
        heading: "Tables",
        items: [
          { label: "Coffee Tables", href: "/category/coffee-tables" },
          { label: "End & Side Tables", href: "/category/end-tables" },
          { label: "Console Tables", href: "/category/console-tables" },
          { label: "TV Stands", href: "/category/tv-stands" },
        ],
      },
      {
        heading: "Storage & Display",
        items: [
          { label: "Cabinets", href: "/category/cabinets" },
          { label: "Sideboards", href: "/category/sideboards" },
          { label: "Bookcases", href: "/category/bookcases" },
          { label: "Wall Mounted Shelves", href: "/category/wall-mounted-shelves" },
          { label: "Room Dividers", href: "/category/room-dividers" },
          { label: "Shoe Racks", href: "/category/shoe-racks" },
          { label: "Coat Racks & Hooks", href: "/category/coat-racks" },
        ],
      },
      {
        heading: "Accessories",
        items: [
          { label: "Floor Lamps", href: "/category/floor-lamps" },
          { label: "Area Rugs", href: "/category/area-rugs" },
          { label: "Fireplaces", href: "/category/fireplaces" },
          { label: "Pet Beds & Feeders", href: "/category/pet-beds" },
        ],
      },
    ],
  },
  {
    label: "Bedroom & Bath",
    href: "/category/bed-bath",
    images: [
      { src: collectionBedroom, alt: "Bedroom collection", caption: "Shop Bedroom", href: "/category/bedroom" },
    ],
    columns: [
      {
        heading: "Beds",
        items: [
          { label: "All Bedroom", href: "/category/bed-bath" },
          { label: "All Beds", href: "/category/beds" },
          { label: "Standard Beds", href: "/category/standard-beds" },
          { label: "Bunk & Loft Beds", href: "/category/bunk-beds" },
          { label: "Bed Sheets", href: "/category/bed-sheets" },
          { label: "Bed Pillows", href: "/category/bed-pillows" },
          { label: "Comforters, Quilts & Duvets", href: "/category/comforters-duvets" },
        ],
      },
      {
        heading: "Bedroom Furniture",
        items: [
          { label: "All Bedroom Furniture", href: "/category/bedroom" },
          { label: "Dressers", href: "/category/dressers" },
          { label: "Nightstands & Bedside Tables", href: "/category/nightstands" },
          { label: "Bedroom Sets", href: "/category/bedroom-sets" },
          { label: "Vanity Tables", href: "/category/vanity-tables" },
          { label: "Headboards", href: "/category/headboards" },
          { label: "Benches", href: "/category/benches" },
          { label: "Kids Chairs", href: "/category/kids-chairs" },
          { label: "Curtains & Drapes", href: "/category/curtains-drapes" },
          { label: "Jewelry Boxes", href: "/category/jewelry-boxes" },
        ],
      },
      {
        heading: "Mattresses",
        items: [
          { label: "All Mattresses", href: "/category/mattresses" },
          { label: "Memory Foam & Hybrid", href: "/category/foam-hybrid-mattresses" },
          { label: "Mattress Toppers", href: "/category/mattress-toppers" },
          { label: "Air Mattress", href: "/category/air-mattress" },
        ],
      },
      {
        heading: "Bathroom",
        items: [
          { label: "All Bathroom", href: "/category/bathroom" },
          { label: "Bathroom Vanities", href: "/category/bathroom-vanities" },
          { label: "Storage & Linen Cabinets", href: "/category/bathroom-storage" },
          { label: "Shower Doors", href: "/category/shower-doors" },
          { label: "Shower Curtains", href: "/category/shower-curtains" },
          { label: "Shower Benches", href: "/category/shower-benches" },
          { label: "Bathtubs", href: "/category/bathtubs" },
          { label: "Bath Towels & Accessories", href: "/category/bath-towels" },
          { label: "Bath Rugs", href: "/category/bath-rugs" },
        ],
      },
    ],
  },
  {
    label: "Dining",
    href: "/category/dining",
    images: [
      { src: collectionDining, alt: "Dining room", caption: "Shop Dining", href: "/category/dining" },
      { src: collectionDiningTables, alt: "Dining tables", caption: "Dining Tables", href: "/category/dining-tables" },
    ],
    columns: [
      {
        heading: "Dining",
        items: [
          { label: "All Dining", href: "/category/dining" },
          { label: "Dining Tables", href: "/category/dining-tables" },
          { label: "Dining Chairs", href: "/category/dining-chairs" },
          { label: "Dining Sets", href: "/category/dining-sets" },
          { label: "Sideboards & Servers", href: "/category/sideboards" },
          { label: "Bar & Counter Chairs", href: "/category/bar-chairs" },
          { label: "Tabletop & Bar", href: "/category/tabletop" },
        ],
      },
      {
        heading: "Kitchen",
        items: [
          { label: "Pantry Cabinets", href: "/category/pantry-cabinets" },
          { label: "Kitchen Islands & Carts", href: "/category/kitchen-islands" },
          { label: "Wine & Beverage Coolers", href: "/category/wine-coolers" },
        ],
      },
      {
        heading: "Accessories",
        items: [
          { label: "Chandeliers", href: "/category/chandeliers" },
          { label: "Trays", href: "/category/trays" },
          { label: "Area Rugs", href: "/category/area-rugs" },
        ],
      },
    ],
  },
  {
    label: "Office",
    href: "/category/office",
    images: [
      { src: collectionOffice, alt: "Home office", caption: "Shop Office", href: "/category/office" },
      { src: collectionOfficeChairs, alt: "Office chairs", caption: "Office Chairs", href: "/category/office-chairs" },
      { src: collectionBookcases, alt: "Bookcases", caption: "Bookcases", href: "/category/bookcases" },
    ],
    columns: [
      {
        heading: "Office",
        items: [
          { label: "All Office", href: "/category/office" },
          { label: "Desks", href: "/category/desks" },
          { label: "Office Chairs", href: "/category/office-chairs" },
          { label: "Bookcases", href: "/category/bookcases" },
          { label: "Filing Cabinets", href: "/category/filing-cabinets" },
        ],
      },
    ],
  },
  {
    label: "Decor",
    href: "/category/decor",
    images: [
      { src: collectionDecor, alt: "Decor accessories", caption: "Shop Decor", href: "/category/decor" },
      { src: collectionMirrors, alt: "Mirrors", caption: "Mirrors", href: "/category/mirrors" },
      { src: collectionWallDecor, alt: "Wall art and decor", caption: "Wall Art & Decor", href: "/category/wall-decor" },
    ],
    // Three short columns, not one of eighteen links: the dropdown hangs below
    // the header and is as tall as its longest column, so on a 1366x768 laptop
    // the last links of a long column ended below the screen.
    columns: [
      {
        heading: "Decor & Accents",
        items: [
          { label: "All Decor", href: "/category/decor" },
          { label: "Mirrors", href: "/category/mirrors" },
          { label: "Vases", href: "/category/vases" },
          { label: "Planters", href: "/category/planters" },
          { label: "Candle Holders", href: "/category/candle-holders" },
          { label: "Candles", href: "/category/candles" },
          { label: "Sculptures & Decor", href: "/category/sculptures" },
          { label: "Trays", href: "/category/trays" },
          { label: "Baskets & Storage Boxes", href: "/category/baskets-organizers" },
        ],
      },
      {
        heading: "Wall Decor",
        items: [
          { label: "Wall Art & Decor", href: "/category/wall-decor" },
          { label: "Wall Clocks", href: "/category/wall-clocks" },
          { label: "Picture Frames", href: "/category/picture-frames" },
          { label: "Peel & Stick Tiles", href: "/category/peel-and-stick-tiles" },
        ],
      },
      {
        heading: "Textiles & Seasonal",
        items: [
          { label: "Throw Pillows", href: "/category/throw-pillows" },
          { label: "Throws & Blankets", href: "/category/throws-blankets" },
          { label: "Doormats", href: "/category/doormats" },
          { label: "Ornaments", href: "/category/ornaments" },
          { label: "Christmas Trees", href: "/category/christmas-trees" },
        ],
      },
    ],
  },
  {
    label: "Outdoor",
    href: "/category/outdoor",
    images: [
      { src: collectionOutdoor, alt: "Outdoor furniture", caption: "Shop Outdoor", href: "/category/outdoor" },
    ],
    columns: [
      {
        heading: "Outdoor Seating",
        items: [
          { label: "All Outdoor", href: "/category/outdoor" },
          { label: "Patio Sets", href: "/category/patio-sets" },
          { label: "Outdoor Sofas", href: "/category/outdoor-sofas" },
          { label: "Outdoor Chairs", href: "/category/outdoor-chairs" },
          { label: "Outdoor Ottomans", href: "/category/outdoor-ottomans" },
          { label: "Outdoor Tables", href: "/category/outdoor-tables" },
        ],
      },
      {
        heading: "Outdoor Living",
        items: [
          { label: "Outdoor Rugs", href: "/category/outdoor-rugs" },
          { label: "Outdoor Planters", href: "/category/outdoor-planters" },
          { label: "Privacy Screens", href: "/category/outdoor-privacy-screens" },
          { label: "Outdoor Umbrellas", href: "/category/outdoor-umbrellas" },
          { label: "Outdoor Heaters", href: "/category/outdoor-heaters" },
          { label: "Fireplaces & Fire Pits", href: "/category/outdoor-fireplaces" },
          { label: "Outdoor Hanging Lights", href: "/category/outdoor-hanging-lights" },
          { label: "Fire Pit Accessories", href: "/category/fire-pit-accessories" },
          { label: "Outdoor Accessories", href: "/category/outdoor-accessories" },
        ],
      },
    ],
  },
  {
    label: "Lighting",
    href: "/category/lighting",
    images: [
      { src: collectionChandeliers, alt: "Chandeliers", caption: "Statement Lighting", href: "/category/chandeliers" },
      { src: collectionFloorLamps, alt: "Floor lamps", caption: "Floor Lamps", href: "/category/floor-lamps" },
      { src: collectionTableLamps, alt: "Table lamps", caption: "Table Lamps", href: "/category/table-lamps" },
    ],
    columns: [
      {
        heading: "Lighting",
        items: [
          { label: "All Lighting", href: "/category/lighting" },
          { label: "Chandeliers", href: "/category/chandeliers" },
          { label: "Floor Lamps", href: "/category/floor-lamps" },
          { label: "Table Lamps", href: "/category/table-lamps" },
          { label: "Outdoor Lighting", href: "/category/outdoor-hanging-lights" },
          { label: "Pendant Lights", href: "/category/pendant-lights" },
          { label: "Wall Lighting", href: "/category/wall-lighting" },
          { label: "Lampshades", href: "/category/lampshades" },
        ],
      },
    ],
  },
  {
    label: "Rugs",
    href: "/category/area-rugs",
    images: [
      { src: collectionRugs, alt: "Area rugs", caption: "Explore Rugs", href: "/category/area-rugs" },
      { src: collectionOutdoorRugs, alt: "Outdoor rugs", caption: "Outdoor Rugs", href: "/category/outdoor-rugs" },
    ],
    columns: [
      {
        heading: "Rugs",
        items: [
          { label: "All Rugs", href: "/category/area-rugs" },
          { label: "Outdoor Rugs", href: "/category/outdoor-rugs" },
          { label: "Bath Rugs", href: "/category/bath-rugs" },
          { label: "Doormats", href: "/category/doormats" },
          { label: "Rug Pads", href: "/category/rug-pads" },
        ],
      },
    ],
  },
  {
    label: "Room Bundles",
    href: "/design-your-room",
  },
  {
    label: "Get the Look",
    href: "/get-the-look",
  },
  {
    label: "Style Quiz",
    href: "/style-quiz",
  },
];

/**
 * The menu as shown: no rooms measured empty, and only links to categories
 * with a real assortment. Shared by the desktop bar below and the phone and
 * tablet menu in Header.tsx. Call it during render, after
 * useCategoryStockVersion(), so it follows the measured stock.
 */
export function visibleMegaMenu(): MegaMenuItem[] {
  return megaMenuData
    .filter((item) => !isEmptyCategoryHref(item.href))
    .map((item) => ({
      ...item,
      images: item.images ? withoutEmptyCategories(item.images) : undefined,
      columns: item.columns
        ?.map((col) => ({ ...col, items: withoutThinCategories(col.items) }))
        .filter((col) => col.items.length > 0),
    }));
}

const dropdownVariants = {
  hidden: { opacity: 0, y: -4 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.2, ease: [0.25, 0.1, 0.25, 1] as const },
  },
  exit: {
    opacity: 0,
    y: -4,
    transition: { duration: 0.15, ease: [0.25, 0.1, 0.25, 1] as const },
  },
};

interface MegaMenuProps {
  variant?: "default" | "home";
  className?: string;
}

export const MegaMenu = ({ variant = "default", className }: MegaMenuProps = {}) => {
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openMenu = useCallback((label: string) => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
      closeTimeout.current = null;
    }
    setActiveMenu(label);
  }, []);

  const scheduleClose = useCallback(() => {
    if (closeTimeout.current) clearTimeout(closeTimeout.current);
    closeTimeout.current = setTimeout(() => setActiveMenu(null), 180);
  }, []);

  const cancelClose = useCallback(() => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
      closeTimeout.current = null;
    }
  }, []);

  // Categories measured to have zero in-stock products are never advertised.
  useCategoryStockVersion();
  const menuData = visibleMegaMenu();

  const activeItem = menuData.find((item) => item.label === activeMenu);
  const hasDropdown = activeItem?.columns;

  const isHome = variant === "home";
  // Not `relative`: the panel and its hover bridge below are placed against the
  // sticky header, which is as wide as the window. Centred on this nav with a
  // 100vw width they sat a few pixels to one side (the nav is not centred in
  // the header), leaving a strip of page showing beside the panel.
  const navClass = isHome
    ? `hidden lg:flex items-center justify-center gap-1.5 xl:gap-4 2xl:gap-6 flex-1 min-w-0 h-[60px] ${className ?? ""}`
    : `hidden md:flex items-center gap-4 lg:gap-6 xl:gap-7 h-11 border-t border-border ${className ?? ""}`;
  // No letter-spacing below xl: at 1024px the eleven tabs fill the row to the
  // pixel, and the 44px icon buttons beside them need the ~19px it frees.
  // after:… makes the 39px tab a 45px-tall tap target without moving the text
  // or the indicator under it (a 1024px tablet shows these tabs to a finger).
  const linkClass = (item: MegaMenuItem) =>
    isHome
      ? `px-0 py-3 text-[10px] xl:text-[11px] tracking-normal xl:tracking-wide uppercase font-bold transition-colors whitespace-nowrap relative after:absolute after:inset-x-0 after:-inset-y-[3px] after:content-[''] ${
          item.highlight
            ? "text-primary hover:text-primary/80"
            : activeMenu === item.label
            ? "text-primary"
            : "text-foreground hover:text-primary"
        }`
      : `px-1 py-2 text-[13px] lg:text-[14px] font-medium tracking-[0.01em] transition-colors whitespace-nowrap relative ${
          item.highlight
            ? "text-destructive hover:text-destructive/80"
            : activeMenu === item.label
            ? "text-primary"
            : "text-foreground hover:text-primary"
        }`;

  return (
    <nav
      className={navClass}
      onMouseLeave={scheduleClose}
    >
      {menuData.map((item) => (
        <div
          key={item.label}
          className="relative h-full flex items-center z-50"
          onMouseEnter={() =>
            item.columns ? openMenu(item.label) : scheduleClose()
          }
        >
          <Link
            to={item.href}
            onClick={() => setActiveMenu(null)}
            onFocus={() => item.columns && openMenu(item.label)}
            onBlur={scheduleClose}
            className={linkClass(item)}
          >
            {item.label}
            {activeMenu === item.label && !item.highlight && (
              <motion.span
                layoutId="mega-menu-indicator"
                className="absolute -bottom-[5px] left-0 right-0 h-[2px] bg-primary"
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
              />
            )}
          </Link>
        </div>
      ))}

      {/* Invisible hover bridge between tabs and dropdown */}
      {hasDropdown && (
        <div
          className="absolute left-0 right-0 top-full h-2 z-40"
          onMouseEnter={cancelClose}
        />
      )}

      {/* The panel never grows past the window: 7rem covers the announcement bar
          plus this row (about 100px at most), and a longer panel scrolls. */}
      <AnimatePresence>
        {hasDropdown && activeItem && (
          <motion.div
            key={activeItem.label}
            variants={dropdownVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="absolute top-full left-0 right-0 max-h-[calc(100vh-7rem)] overflow-y-auto bg-background border-b border-border shadow-lg z-40"
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
          >
            <MegaMenuDropdown
              item={activeItem}
              onClose={() => setActiveMenu(null)}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};
